var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import styles from './files.styles';
import { ifDefined } from 'lit/directives/if-defined.js';
import { dictionary, formatFileSize } from '@mf-cms/helpers';
let PFiles = class PFiles extends LitElement {
    constructor() {
        super(...arguments);
        this.textAlign = 'left';
        this.sortOrder = 'asc';
        this.culture = 'en-GB';
        this.files = [];
        this.initialId = 0; //set inital folder id, needed for change in the admin
        this.initialSort = 'name';
        this.initialSortOrder = 'asc';
        this.showLoadMoreFilesButton = false;
        this.initialSearchLimit = 7;
        this.showMoreFilesLoading = false;
        this.showEmptyMessage = false;
    }
    static { this.styles = styles; }
    /**
     * First updated lifecycle
     * @returns {void}
     */
    async firstUpdated() {
        this.accessToken = window.getAccessToken();
        if (this.enableSearch) {
            this.files = await this.searchFiles(this.searchQuery, this.initialSearchLimit);
        }
        else if (this.folderId) {
            this.initialId = parseInt(this.folderId);
            this.initialSort = this.sort;
            this.initialSortOrder = this.sortOrder;
            this.files = await this.getFiles(parseInt(this.folderId));
        }
    }
    /**
     * Updated lifecycle
     * @returns {void}
     */
    async updated() {
        if (this.enableSearch) {
            return;
        }
        // clear files if folderId is removed
        if (!this.folderId && this.files.length > 0) {
            this.initialId = 0;
            this.files = [];
            return;
        }
        if (!this.folderId) {
            return;
        }
        // get videos if folderId is set to a new value
        if (this.initialId !== parseInt(this.folderId)) {
            this.initialId = parseInt(this.folderId);
            this.files = await this.getFiles(parseInt(this.folderId));
        }
        // get videos if sort is set to a new value
        if ((this.sort && this.initialSort !== this.sort) ||
            (this.sortOrder && this.initialSortOrder !== this.sortOrder)) {
            this.initialSort = this.sort;
            this.initialSortOrder = this.sortOrder;
            this.files = await this.getFiles(parseInt(this.folderId));
        }
    }
    /**
     * Get files
     * @param {number} folderId - Folder id
     * @returns {Array} Files
     */
    async getFiles(folderId) {
        try {
            // TODO: This should not use fields=any
            const response = await fetch(`${process.env.API_URL}/folder/${folderId}/files?fields=any&access_token=${this.accessToken}&sort=${this.sort}&order=${this.sortOrder}`);
            if (response.status === 404) {
                return [];
            }
            const json = await response.json();
            return json;
        }
        catch (e) {
            console.error(e);
        }
        return [];
    }
    /**
     * Search files
     * @param {string} query - Query
     * @param {number} limit - Limit
     * @param {boolean} showLoading - Show loading
     * @returns {Array} Files
     */
    async searchFiles(query, limit, showLoading = true) {
        if (query == '') {
            this.showEmptyMessage = true;
            return [];
        }
        // TODO: This should not use fields=any
        this.loading = showLoading;
        const response = await fetch(`${process.env.API_URL}/search/file?fields=any&access_token=${this.accessToken}${limit ? `&page=1&per_page=${limit}` : ''}&sort=${this.sort}&order=${this.sortOrder}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                searchTerm: query,
                ai: true
            })
        });
        if (response.status !== 200) {
            this.loading = false;
            this.showEmptyMessage = true;
            return [];
        }
        const json = await response.json();
        this.loading = false;
        // Show load more files button if there are more than set limit
        if (json.length > 6 && limit !== 0) {
            this.showLoadMoreFilesButton = true;
            // Remove last file
            return json.slice(0, 6);
        }
        else {
            this.showLoadMoreFilesButton = false;
        }
        // Show empty message if there are no files
        this.showEmptyMessage = json.length === 0;
        return json;
    }
    /**
     * Show all files in search
     * @returns {void}
     */
    async showAllFiles() {
        this.showMoreFilesLoading = true;
        this.files = await this.searchFiles(this.searchQuery, 0, false);
        this.render();
        this.showMoreFilesLoading = false;
        // Get last file before load and set focus on it
        const targetFile = this.shadowRoot?.querySelectorAll('p-file')[this.initialSearchLimit - 2];
        if (targetFile) {
            targetFile.setFocus();
        }
    }
    /**
     * Render component
     * @returns {TemplateResult} Template
     */
    render() {
        const colCount = [this.showSize, this.showType, this.showDimensions].filter(Boolean).length;
        return html ` <section class="files data-count-${colCount}"> <div class="container"> ${this.hideHeader ? '' : html `
                <div class="header text-align-${this.textAlign}">
                  <slot name="header"></slot>
                </div>
              `}
          ${this.layout == 'list'
            ? html ` <div class="files-list-header"> <div class="files-list-header-item"> ${dictionary(this.culture, 'file', 'file')} </div> ${this.showDimensions ? html `<div class="files-list-header-item">
                        ${dictionary(this.culture, 'file', 'dimensions')}
                      </div>`
                : ''}
                  ${this.showType
                ? html `<div class="files-list-header-item"> ${dictionary(this.culture, 'file', 'type')} </div>`
                : ''}
                  ${this.showSize
                ? html `<div class="files-list-header-item"> ${dictionary(this.culture, 'file', 'size')} </div>`
                : ''}
                </div>
              `
            : ''}
          <div class="files-wrap layout-${this.layout}">
            ${this.loading
            ? html `${Array(this.loadingItems).fill(0).map(() => html `<p-file loading></p-file>`)}`
            : this.files.map((file) => html ` <p-file image-thumb=${file.thumbPreview == null ? this.getFileIconPath(file.type.id) : file.thumbPreview} image-small=${file.smallPreview == null ? this.getFileIconPath(file.type.id) : file.smallPreview} image-medium=${file.mediumPreview == null ? this.getFileIconPath(file.type.id) : file.mediumPreview} ?icon=${file.thumbPreview == null} name=${file.name} ratio=${file.width / file.height} dimensions=${this.getDimensions(file.width, file.height, file.type.id, file.type.extension)} width=${file.width} height=${file.height} type=${this.getType(file.type.extension)} size=${ifDefined(file.filesize ? formatFileSize(file.filesize, this.culture) : undefined)} ?transparent=${file.alpha && this.checkeredBackground} ?video=${file.type.type == 'video'} file-id=${file.id} culture=${this.culture} ?show-size=${this.showSize} ?show-type=${this.showType} ?show-dimensions=${this.showDimensions} layout=${this.layout} ?checkered-background=${this.checkeredBackground} > </p-file> `)}
          </div>
          ${this.showEmptyMessage
            ? html `<p class="empty">${dictionary(this.culture, 'search', 'noResults')}</p>`
            : ''}
          ${this.showLoadMoreFilesButton
            ? html `<br /><p-button-group alignment="center" ><p-button ?loading=${this.showMoreFilesLoading} @p-click=${this.showAllFiles} color="border" >${dictionary(this.culture, 'search', 'showAll')}</p-button ></p-button-group >`
            : ''}
        </div>
      </section>
    `;
    }
    /**
     * Returns the type of the file
     * @private
     * @param {string} type - Type
     * @returns {string} Formatted Type
     */
    getType(type) {
        if (type == 'mfpd' || type == 'mfdt') {
            return dictionary(this.culture, 'file', 'template');
        }
        return type;
    }
    /**
     * Returns the dimensions of the file
     * @private
     * @param {number} width - Width
     * @param {number} height - Height
     * @param {number} fileTypeId - File Type Id
     * @param {string} type - Type
     * @returns {string} Dimensions
     */
    getDimensions(width, height, fileTypeId, type) {
        if (width == 0 || height == 0) {
            return '';
        }
        // Document or design template
        if ((fileTypeId > 200 && fileTypeId < 210) || type == 'mfpd' || type == 'mfdt') {
            return `${width} x ${height} mm`;
        }
        return `${width} x ${height} px`;
    }
    /**
     * Returns the icon path of the file
     * @private
     * @param {number} fileTypeId - File Type Id
     * @returns {string} Icon Path
     */
    getFileIconPath(fileTypeId) {
        switch (fileTypeId) {
            // Image
            case 101:
                fileTypeId = 101;
                break;
            // Illustrator
            case 204:
                fileTypeId = 204;
                break;
            // Photoshop
            case 290:
                fileTypeId = 290;
                break;
            // MP4
            case 311:
                fileTypeId = 311;
                break;
            // Font
            case 401:
            case 402:
            case 410:
                fileTypeId = 401;
                break;
            // Presentation
            case 451:
            case 453:
            case 454:
            case 455:
            case 456:
                fileTypeId = 451;
                break;
            // Document
            case 501:
            case 502:
            case 504:
            case 505:
            case 506:
            case 507:
            case 570:
            case 575:
            case 576:
            case 577:
            case 591:
            case 801:
            case 802:
                fileTypeId = 501;
                break;
            // Spreadsheet
            case 520:
            case 521:
            case 522:
            case 523:
            case 571:
                fileTypeId = 520;
                break;
            // Indesign
            case 550:
            case 551:
            case 552:
                fileTypeId = 550;
                break;
            // Live
            case 585:
                fileTypeId = 585;
                break;
            // srt, vtt
            case 592:
            case 593:
                fileTypeId = 592;
                break;
            // Audio
            case 651:
            case 652:
            case 654:
            case 655:
            case 656:
            case 657:
            case 658:
            case 659:
            case 660:
            case 661:
            case 662:
            case 663:
                fileTypeId = 651;
                break;
            // Compressed file (zip, rar etc)
            case 701:
            case 702:
            case 703:
            case 704:
            case 705:
                fileTypeId = 701;
                break;
            default:
                fileTypeId = 999;
        }
        return `https://mfstatic.com/images/icons/filetype-${fileTypeId}-dark.svg`;
    }
};
__decorate([
    property({ type: String, reflect: true, attribute: 'text-align' })
], PFiles.prototype, "textAlign", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'folder-id' })
], PFiles.prototype, "folderId", void 0);
__decorate([
    property({ type: String, reflect: true })
], PFiles.prototype, "sort", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'sort-order' })
], PFiles.prototype, "sortOrder", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'hide-header' })
], PFiles.prototype, "hideHeader", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-size' })
], PFiles.prototype, "showSize", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-type' })
], PFiles.prototype, "showType", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-dimensions' })
], PFiles.prototype, "showDimensions", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'checkered-background' })
], PFiles.prototype, "checkeredBackground", void 0);
__decorate([
    property({ type: String, reflect: true })
], PFiles.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], PFiles.prototype, "culture", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'enable-search' })
], PFiles.prototype, "enableSearch", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'search-query' })
], PFiles.prototype, "searchQuery", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'loading' })
], PFiles.prototype, "loading", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: 'loading-items' })
], PFiles.prototype, "loadingItems", void 0);
__decorate([
    property()
], PFiles.prototype, "files", void 0);
__decorate([
    state()
], PFiles.prototype, "showMoreFilesLoading", void 0);
__decorate([
    state()
], PFiles.prototype, "showEmptyMessage", void 0);
PFiles = __decorate([
    customElement('p-files')
    /**
     * PFiles component
     *
     * @class PFiles
     * @typedef {PFiles}
     * @extends {LitElement}
     */
], PFiles);
export { PFiles };
